/**
 * SFrame170 – Initiate Probate & Legal Authorities: Phase Overview
 *
 * phase4_probate_structure: section hierarchy, phase progress, checklist tracker, time estimates.
 * phase4_probate_forms: step detail pages use structured fields + documents (see settlementFlowConfig probate).
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PhaseOverview, type PhaseStepState } from '../settlement';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { SETTLEMENT_PHASE_CONFIGS } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

const config = SETTLEMENT_PHASE_CONFIGS['probate'];

// Reduced flow: only two high-level steps for the probate phase.
const stepRoutes: Record<string, string> = {
  'prob-1': '/sframe-171',
  'prob-2': '/sframe-172',
};

export default function SFrame170() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { loadPhaseProgress, error } = useSettlementSession(estateId ?? null);

  const [steps, setSteps] = useState<PhaseStepState[]>(
    config.steps.map((step) => ({
      step,
      status: 'not-started',
      subTaskProgress: {
        completed: 0,
        total: step.subTasks?.reduce((sum, st) => sum + (st.checklistItems?.length || 0), 0) || 0,
      },
    }))
  );

  useEffect(() => {
    if (!estateId) return;
    const load = async () => {
      const progressData = await loadPhaseProgress('probate');
      setSteps(
        config.steps.map((step) => {
          const progress = progressData.find((p) => p.stepId === step.id);
          const totalItems =
            step.subTasks?.reduce((sum, st) => sum + (st.checklistItems?.length || 0), 0) || 0;
          return {
            step,
            status: (progress?.status as PhaseStepState['status']) || 'not-started',
            subTaskProgress: {
              completed: progress?.completedChecklistItems?.length || 0,
              total: totalItems,
            },
          };
        })
      );
    };
    load();
  }, [estateId, loadPhaseProgress]);

  const handleStepClick = (stepId: string) => {
    const route = stepRoutes[stepId];
    if (route) navigate(estateId ? `${route}/${estateId}` : route);
  };

  const phaseChecklist = useMemo(() => {
    const completed = steps.reduce((a, s) => a + (s.subTaskProgress?.completed || 0), 0);
    const total = steps.reduce((a, s) => a + (s.subTaskProgress?.total || 0), 0);
    return { label: 'Checklist items tracked in this phase', completed, total };
  }, [steps]);

  const remainingMinutes = steps
    .filter((s) => s.status !== 'complete')
    .reduce((sum, s) => sum + (s.step.estimatedMinutes || 0), 0);

  return (
    <>
      {error && (
        <div className={`${SETTLEMENT_PLANNER_LAYOUT.container} mb-4`}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        </div>
      )}
      <PhaseOverview
        eyebrow=""
        title="Get Legal Authority to Act on the Estate"
        subtitle="Determine whether probate is required and complete the steps needed to obtain legal authority from the court."
        intro={
          'Probate gives an executor or administrator the legal authority to manage the estate. This process may vary based on the estate, ownership of assets, and whether a will or trust exists.'
        }
        layoutVariant="planner"
        sectionOrder="next-first"
        phaseChecklist={phaseChecklist.total > 0 ? phaseChecklist : undefined}
        steps={steps}
        estimatedMinutes={remainingMinutes}
        onStepClick={handleStepClick}
      />
    </>
  );
}
